export interface ISearchRequestSubset {
    where?: { [id: string]: any };
    order?: string[]; // name ASC
    limit?: number;
    bCountOnly?: boolean;
}

export interface ISearch {
    searchName: string;
    renderOptions: ISearchRenderOptions; // put resource name, icon name, .. UI specific stuff in here.

    searchProfileIdDefault: string;
    searchProfiles: ISearchProfile[];

    autoSearch?: boolean;
    autoSearchProfileId?: string;
    boOptions?: { [id: string]: any };

    [id: string]: any; // Allow for constants to represent profile ids..  i.e. ProfileId_BasicSearch = "default"
}

export interface ISearchRenderOptions {}

export interface IDisplayJoinField {
    columnTitle: string;
    seperator: string;
    fields: string[];
    alignment?: HorizAlignment;
    columnWidthCharacters?: number;
}

export interface ILockField {
    columnTitle: string;
    lockClass: string;
    lockIdFieldName: string;
    bDisplayLockIcon: boolean;
    bDisplayLockOwner: boolean;
}

export interface ISearchProfile {
    profileId: string;
    profileName: string;
    renderOptions: ISearchProfileRenderOptions; // put resource name, UI specific stuff in here.

    profileFilter: IProfileFilter[]; // defines filter fields
    profileDisplayFields: (string | IDisplayJoinField | ILockField)[]; // Search Provider Field Name (Use to Generate Grid Columns)
    profileHiddenFields?: (string | IDisplayJoinField | ILockField)[];
    profileRequiredFields?: (string | IDisplayJoinField | ILockField)[];
    profileHyperlinkFields?: string[]; // ColumnFieldName or JoinField column Title
    profileTotals?: ISearchResultsTotalSpecification[];

    profileSearchProvider: ISearchProvider;
    profileSearchLimit?: number;

    profileTreeIdDefault?: string;
    profileTrees?: ITree[];

    // Do sorting last.. not necessary for phase 1
    profileSortIdDefault: string;
    profileSorts: ISearchSort[];
}

export interface ISearchProfileRenderOptions {}

export interface IProfileFilter {
    profileFilterName: string;
    renderOptions: IProfileFilterRenderOptions; // put resource name, icon name, .. UI specific stuff in here.

    searchProviderFieldId: string; // get data type from searchProviderFields..

    bMultiSelect?: boolean;
    bQuickSearch?: boolean;
    bHidden?: boolean;
    bRequired?: boolean;
}

export interface IProfileFilterRenderOptions {}

export interface ISearchResultsTotalSpecification {
    id: string;
    name: string;
    renderOptions?: ISearchResultsTotalSpecificationRenderOptions;
    aggregateByFields: string[];
    sumFields?: string[];
    countFields?: string[]; // distinct values
    minFields?: string[];
    maxFields?: string[];
    rowCountField?: string;
    levelSums?: { levelFields: string[]; sumFields: string[] };
    constantFieldValues?: { [id: string]: string };
    firstValueFields?: string[];
    placement: "Begin" | "End" | "Inline";
    orderByFields?: string[]; // Only for Inline
}

export interface ISearchResultsTotalSpecificationRenderOptions {}

export interface ISearchSort {
    sortId: string;
    sortName: string;
    renderOptions: ISearchSortRenderOptions; // put resource name, icon name, .. UI specific stuff in here.

    sortFields: { searchProviderFieldId: string; sortType: string }[];
}

export interface ISearchSortRenderOptions {}

export interface ITree {
    treeId: string;
    treeName: string;
    renderOptions: ITreeRenderOptions; // put resource name, icon name, .. UI specific stuff in here.

    nodeProviders: ITreeNodeProvider[]; // get data type from searchProviderFields..
}

export interface ITreeRenderOptions {
    ui5?: {
        folderIconOpen?: string;
        folderIconClosed?: string;
        folderIconClosedWithChildrenSelected?: string;
        folderIconClosedWithAllChildrenSelected?: string;
        leafIcon?: string;
    };

    [id: string]: any;
}

export const TreeRenderOptionsDefault: ITreeRenderOptions = {
    ui5: {
        folderIconOpen: "sap-icon://open-folder",
        folderIconClosed: "sap-icon://folder-blank",
        folderIconClosedWithChildrenSelected: "sap-icon://folder",
        folderIconClosedWithAllChildrenSelected: "sap-icon://folder-full",
        leafIcon: "sap-icon://chevron-phase",
    },
};

export interface ITreeNodeProvider {
    getAllParents(wb_id: number | string, dataRow: any): Promise<ITreeNode[]>;
    getChildren?(wb_id: number | string, dataRow: any): Promise<ITreeNode[]>;
}

export interface ITreeNode {
    id: string;
    name: string;
    toolTip?: string;
}

export interface ISearchProvider {
    readonly searchProviderFields: { [field_id: string]: ISearchProviderField };

    search(requestSpec: ISearchRequestSubset): Promise<any[]>;

    keyFields: {
        id: string;
        id_ui: string;
        name: string;
        source_id?: string;
        wb_source_id?: string;
    };

    defaultLimit?: number;

    distinctValues?(fieldName: string, condition?: any): Promise<string[] | number[]>;
}

export interface ISearchProviderField {
    type: SearchProviderFieldType; // string | number | date | sapdate | boolean
    domainOptions?: ISearchProviderFieldDomainOptions;
    renderOptions?: ISearchProviderFieldRenderOptions;
}

export interface ISearchProviderFieldRenderOptions {
    columnName: string;
    columnResourceName?: string;
    numberFormat?: string;
    dateFormat?: string;
    alignment?: HorizAlignment;
    columnWidthCharacters?: number;
    linkWbIdFieldName?: string;
    linkAppExecId?: string;
}

export interface ISearchProviderFieldDomainOptions {
    lowerLimit?: number;
    upperLimit?: number;
    maxLength?: number;
    domainIds?: string | string[];
    domainItemSubCode?: string | string[];
    metaTypeIds?: string | string[];
    useIdAsKey?: boolean;
    bUseSearchProvidersDistinctValues?: boolean;
    search?: ISearch;
    profileId?: string;
    searchCondition?: { [id: string]: any };
    source?: boolean;
    spendType?: boolean;
    boOptionField?: string;
}

export enum SearchProviderFieldType {
    number = "number",
    string = "string",
    date = "date",
    sapdate = "sapdate",
    saptimestamp = "saptimestamp",
    boolean = "boolean",
}

export enum HorizAlignment {
    Left = "Left",
    Right = "Right",
    Center = "Center",
}

export interface ISearchRendererOptions {
    selectedValues?: number[] | string[];
    appId?: string;
    profiles?: string[]; // not implemented in control yet
    autoSearchOnly?: boolean;
    autoSearch?: boolean;
    autoSearchProfileId?: string;
    treeViewFirst?: boolean;
    referenceControl?: any;
    filterCondition?: { [fieldName: string]: any };
    selectionLimit?: number;
}

export interface ISearchRenderer {
    SelectOne(aSearch: ISearch, options?: ISearchRendererOptions): Promise<number | string>;
    SelectMany(aSearch: ISearch, options?: ISearchRendererOptions): Promise<number[] | string[]>;
    SelectNavigationList(aSearch: ISearch, options?: ISearchRendererOptions): Promise<number[] | string[]>;
    SelectHyperlink(aSearch: ISearch, options?: ISearchRendererOptions): Promise<void>;
}

export enum SelectModes {
    SelectOne = "SelectOne",
    SelectMany = "SelectMany",
    SelectNavigationList = "SelectNavigationList",
    SelectHyperLink = "HyperLink",
    SelectOneHyperLink = "SelectOneHyperLink", // not implemented yet.(intended to close dialog when one is selected.
    None = "None",
}
